import dayjs from 'dayjs';
import environment from '../environment';
import { Product, ProductIdentification } from '../../features/cart/shop-cart/interfaces/shop-cart.interface';
import { formattedJoin, HE, HE_LOWER, isHeStorePurchaseFlow, isStandaloneAleksTunnelPurchase, ordinalSuffixOf } from './util';
import { getProductUrl } from '../../features/product-card/utils/product-card.util';
import { ProductCardData } from '../../features/product-card/product-card-display';
import { SHARPEN_PROGRAM_ID } from '../../features/upsell/constants/sharpen-page-type.constants';
import { isMhpEbook, isMhpPhysical, OfferTypeObject } from '../../features/mhp/utils/mhp.util';
import { MHP_PDP_BASE_PATH } from '../constants/professional-redirects';
import {
  determineBusinessUnit,
  PRODUCT_TYPE_FLASHCARD,
  PROFESSIONAL_FCD_TITLE_TYPE,
} from './analytics-util';
import { OfferType, OfferTypeChecker } from './offer-type-util';
import { PaymentOrderItemData, PaymentOrderItemsProductMetaData } from '../../features/order-confirmation/order-completion.interface';
import { getSharpenCompanionTitle } from '../../features/sharpen/utils/sharpen.util';
import { ProductTermData } from '../../features/product-card/interfaces/product-card.interface';
import { ItemType } from '../../features/product-card/product.interface';
import { mapJsonCreateProductFormat } from '../../features/create-custom/mappings/create-custom.mapping';

export const GO = 'GO';

enum STORE_TYPE {
  PRIVATE = 'PRIVATE',
  CUSTOM = 'CUSTOM',
}

export interface PdpLinkPayload {
  businessUnit: string;
  isbn: string;
  store?: string;
  productType?: string;
  seoTitle?: string;
  mhid?: string;
  programSourceId?: string;
  programSourceName?: string;
  offerType?: string;
}

export interface ProfessionalPLPLink {
  programSourceId: string;
}

export interface ProductDuration {
  duration?: number;
  durationPeriod?: string;
  term?: string;
}

export interface ProductTitleMetadata {
  title: string;
  programSourceId: string;
  businessUnit: string;
  isVirtual: boolean;
  formatTypeDesc: string;
  duration: number;
  durationPeriod: string;
  offerType?: string;
}

export interface HEProductTitleMetadata {
  businessUnit: string;
  isVirtual: boolean;
  formatTypeDesc: string;
  duration: number;
  durationPeriod: string;
  offerType?: string;
}

const offerTypeWithoutDuration = [
  OfferType.GDP,
];

export const DEFAULT_COVER_IMAGE = `${environment().mheducationUrl}/content/dam/mhe/no_cover_250.webp`;
export const buildHeSKU = (isbn: string) => `OH${isbn}`;
export const buildHeSKUOfferPricing = (offerPricingId: string) => `O${offerPricingId}`;
export const buildProfessionalSKU = (isbn: string) => `OP${isbn}`;
export const buildSegSKU = (isbn: string) => `OS${isbn}`;
export const EXTENDED_ACCESS_DURATION = '1440';
export const EXTENDED_ACCESS_VERBOSE = 'Extended Access';

/**
 * Builds the SKU based on the ISBN and business unit.
 *
 * @param isbn
 * @param businessUnit
 */
export function buildSkuFromIsbn(isbn: string, businessUnit: string): string {
  if (['K12', 'SEG'].includes(businessUnit.toUpperCase())) {
    return buildSegSKU(isbn);
  } else if (businessUnit.toUpperCase() === 'PROFESSIONAL') {
    return buildProfessionalSKU(isbn);
  }
  return buildHeSKU(isbn);
}

/**
 * Builds the SKU based on the Offer Pricing ID.
 *
 * @param isbn
 * @param businessUnit
 */
export function buildSkuFromOfferPricingId(offerPricingId: string, businessUnit: string): string {
  if (businessUnit.toUpperCase() !== 'HE') {
    throw Error("Just HE is supported using the Offer Pricing ID")
  }
  return buildHeSKUOfferPricing(offerPricingId);
}

/**
 * Gets the primary ISBN.
 *
 * By order of priority: ISBN13, ISBN10, MHID
 *
 * @param isbn13
 * @param isbn10
 * @param mhid
 */
export function getPrimaryIsbn(isbn13?: string, isbn10?: string, mhid?: string): string {
  if (isbn13 && isbn13 !== 'N/A') {
    return isbn13;
  }

  return isbn10 || mhid || '';
}

export function getImageSrcUrl(isbn: string) {
  return environment().mheducationUrl.concat(`/cover-images/Webp_400-${isbn === SHARPEN_PROGRAM_ID ? 'wide' : 'high'}/`)
    .concat(isbn?.replace('MKTGPRG', ''))
    .concat(`.webp?404URL=${DEFAULT_COVER_IMAGE}`);
}

export function getPdpLink(pdpLinkPayload: PdpLinkPayload) {
  if (
    pdpLinkPayload.businessUnit.toLowerCase() === 'he' &&
    (pdpLinkPayload.productType === STORE_TYPE.CUSTOM || pdpLinkPayload.productType === STORE_TYPE.PRIVATE)
  ) {
    return environment().mheducationUrl.concat('/highered/custom/product/').concat(pdpLinkPayload.isbn).concat('.html');
  } else if (
    pdpLinkPayload.businessUnit.toLowerCase() === 'he' &&
    !isHeStorePurchaseFlow()
  ) {
    if (pdpLinkPayload.programSourceName) {
      return environment()
        .mheducationUrl.concat('/highered/product/')
        .concat(pdpLinkPayload.programSourceName)
        .concat('.html')
        .concat(`?isbn=${pdpLinkPayload.isbn}`);
    } else {
      return environment().mheducationUrl.concat('/highered/product/').concat(pdpLinkPayload.isbn).concat('.html');
    }
  } else if (
    pdpLinkPayload.businessUnit.toLowerCase() === 'professional' &&
    (isMhpEbook({ offerType: pdpLinkPayload.offerType } as OfferTypeObject) ||
      isMhpPhysical({
        businessUnit: pdpLinkPayload.businessUnit,
        offerType: pdpLinkPayload.offerType,
      }))
  ) {
    return pdpLinkPayload.programSourceName
      ? environment()
          .mheducationUrl.concat(MHP_PDP_BASE_PATH)
          .concat(pdpLinkPayload.programSourceName)
          .concat('.html')
          .concat(`?isbn=${pdpLinkPayload.isbn}`)
      : environment().mheducationUrl.concat(MHP_PDP_BASE_PATH).concat(pdpLinkPayload.isbn).concat('.html');
  } else if (pdpLinkPayload.businessUnit.toLowerCase() === 'seg') {
    return environment().mheducationUrl.concat('/prek-12/product/').concat(pdpLinkPayload.isbn).concat('.html');
  } else if (pdpLinkPayload.businessUnit.toLowerCase() === 'seg-product') {
    return environment()
      .mheducationUrl.concat('/prek-12/product')
      .concat(pdpLinkPayload.seoTitle ? `/${pdpLinkPayload.seoTitle}/` : '')
      .concat(pdpLinkPayload.isbn || pdpLinkPayload.mhid || '')
      .concat('.html');
  } else if (pdpLinkPayload.businessUnit.toLowerCase() === 'seg-store' && pdpLinkPayload.store !== undefined) {
    return environment()
      .mheducationUrl.concat('/prek-12/custom/store/')
      .concat(pdpLinkPayload.store)
      .concat('/product/')
      .concat(pdpLinkPayload.isbn)
      .concat('.html');
  } else if (
    isHeStorePurchaseFlow()
  ) {
    return environment()
      .mheducationUrl.concat('/highered/custom/product/')
      .concat(pdpLinkPayload.isbn)
      .concat('.html');
  } else {
    return '';
  }
}

export function isNationalProduct(product: Product): boolean {
  return !(
    product.productIdentifier.productType?.toUpperCase() == STORE_TYPE.CUSTOM ||
    product.productIdentifier.productType?.toUpperCase() == STORE_TYPE.PRIVATE ||
    OfferTypeChecker.isCreateCustom(product.productMetadata.offerType)
  );
}

/**
 * Verifies if the product is a HE custom store product (Create).
 * The check on productType is kept for compatibility with the old shop implementation.
 *
 * @param product
 */
export function isHeCustomStoreProduct(product: Product): boolean {
  if (product.productIdentifier.businessUnit?.toUpperCase() !== HE) {
    return false;
  }

  return (
    product.productIdentifier.productType?.toUpperCase() == STORE_TYPE.CUSTOM ||
    OfferTypeChecker.isCreateEbook(product.productMetadata.offerType)
  );
}

/**
 * Verifies if the product is a HE private store product (Custom).
 * The check on productType is kept for compatibility with the old shop implementation.
 *
 * @param product
 */
export function isHePrivateStoreProduct(product: Product): boolean {
  if (product.productIdentifier.businessUnit?.toUpperCase() !== HE) {
    return false;
  }

  return (
    product.productIdentifier.productType?.toUpperCase() == STORE_TYPE.PRIVATE ||
    OfferTypeChecker.isCustom(product.productMetadata.offerType)
  );
}

/**
 * Returns the expiration date for a digital product
 *
 * @param duration the duration (number) of the subscription
 * @param period the period of the subscription (day, month, year)
 * @param createdDate the start date for the calculation, if not provided, it will use the current date
 */
export function calculateDurationDigitalProduct(
  duration: number,
  period: string,
  createdDate: number | string = ''
): Date {
  let currentDate = new Date();
  if (createdDate) {
    currentDate = new Date(createdDate);
  }

  if (period === 'Month') {
    currentDate = dayjs(currentDate).add(duration, 'month').toDate();
  } else if (period === 'Year') {
    currentDate = dayjs(currentDate).add(duration, 'year').toDate();
  } else {
    currentDate.setDate(currentDate.getDate() + duration);
  }

  return currentDate;
}

export function isProfessional(businessUnit: string) {
  return 'PROFESSIONAL' === businessUnit.toUpperCase();
}

export function isConnectProduct(product) {
  return product?.application === 'connect';
}

export function isSharpenPlus(programSourceId?: string) {
  return programSourceId === SHARPEN_PROGRAM_ID;
}

export function getProductDetailsForAnalytics(product: ProductCardData, businessUnit: string) {
  return JSON.parse(
    JSON.stringify({
      title: product.title,
      image: product.image,
      url: product.url,
      copyRightYear: product.copyRightYear,
      isbn: product.isbn,
      isbn13: product.isbn13,
      businessUnit: determineBusinessUnit(businessUnit?.toUpperCase()),
      ...(product.titleType === PROFESSIONAL_FCD_TITLE_TYPE && {
        productType: PRODUCT_TYPE_FLASHCARD,
      }),
      programId: product.programId,
      evergreen: product.isEvergreen,
      netPrice: product.netPrice,
      description: product.description,
      author: formattedJoin(product.contributors!),
      edition: product.edition,
      pageCategory: window.mhPageCategory,
      href: `${environment().mheducationUrl}${getProductUrl(product)}`,
    })
  );
}

export function getProductTitleWithoutEdition(
  productTitleMetadata: ProductTitleMetadata,
) {
  let title = productTitleMetadata?.title;
  if (isSharpenPlus(productTitleMetadata?.programSourceId)) {
    // Remove the edition which is coming in the title in case of Sharpen
    // @todo check with BE to fix the data
    title = title?.split(',').filter((part) => !part?.toLowerCase().includes('edition')).join(',');
  }
  if (
    isProductHigheredVirtual(productTitleMetadata?.businessUnit, productTitleMetadata?.isVirtual) &&
    !shouldSkipDurationForOfferType(productTitleMetadata?.offerType!)
  ) {
    return appendDuration(
      productTitleMetadata?.duration,
      productTitleMetadata?.durationPeriod,
      canDisplayExtendedAccess(productTitleMetadata),
      title,
    )
  }
  return title;
}

export function appendedDurationForHEProducts(
  heProductTitleMetadata: HEProductTitleMetadata,
) {
  if (
    isProductHigheredVirtual(heProductTitleMetadata?.businessUnit, heProductTitleMetadata?.isVirtual) &&
    !shouldSkipDurationForOfferType(heProductTitleMetadata?.offerType || '')
  ) {
    return appendDuration(
      heProductTitleMetadata?.duration,
      heProductTitleMetadata?.durationPeriod,
      canDisplayExtendedAccess(heProductTitleMetadata),
    );
  }
  return '';
}

function isProductHigheredVirtual(businessUnit: string, isVirtual: boolean) {
  return businessUnit?.toLowerCase() === HE_LOWER && isVirtual;
}

function canDisplayExtendedAccess(productTitleMetadata: ProductTitleMetadata | HEProductTitleMetadata): boolean {
  return productTitleMetadata.formatTypeDesc?.toLowerCase()?.replace(/\s+/g, '') === OfferType.EBOOK_PURCHASE;
}

export function appendDuration(
  duration: number,
  durationPeriod: string,
  extendedAccess: boolean,
  title?: string,
) {
  if (!duration || !durationPeriod) {
    return title ?? '';
  }

  return extendedAccess 
    ? `${title ? `${title} ` : ''}${EXTENDED_ACCESS_VERBOSE}`
    : durationAppendedProductTitle(duration, durationPeriod, title)
}

const durationAppendedProductTitle = (
  duration: number, 
  durationPeriod: string,
  title?: string
) => {
  if (!duration || !durationPeriod) {
    return '';
  }

  return `${title ? `${title} ` : ''}${duration > 0 ? `${duration} ${durationPeriod}${duration > 1 ? 's': ''}` : '' }`;
}

export function shouldSkipDurationForOfferType(offerType: string) {
  return offerTypeWithoutDuration.includes(offerType?.toLowerCase() as OfferType);
}

/* Appending duration for the tunnel flow
1. If only term is present
  a. check if term === -1 || term === 'always'-> then Extended Access
  b. if both duration and durationPeriod are not present -> title + term + Days
  c. if term and durationPeriod are present (edge case) -> title + term + durationPeriod
2. if the duration and durationPeriod is present:
  a. check if 4 years -> then Extended Access
  b. append simply duration + durationPeriod
*/
export function appendDurationForTunnelPurchaseFlow({
  duration,
  durationPeriod,
  title,
  term,
}: {
  duration?: number;
  durationPeriod?: string;
  title?: string;
  term?: string;
}) {
  if (term && !duration) {
    if (term === '-1' || term?.toLowerCase() === 'always') {
      return `${title ? `${title} ` : ''}${EXTENDED_ACCESS_VERBOSE}`;
    }

    if (!duration && !durationPeriod) {
      // term is coming as -1 or always instead of 4 from RMS, still keeping this a fallback for any edge case
      return `${title ? `${title} ` : ''}` + 
        `${term === '4' ? EXTENDED_ACCESS_VERBOSE: `${term}` +
        `${Number(term) > 1 ? ' Days' : ' Day'}`}`;
    }

    if (durationPeriod) {
      return `${title ? `${title} ` : ''}${term} ${durationPeriod}`;
    }
  }

  if (duration && durationPeriod) {
    if (
      duration === 4 &&
      durationPeriod?.toLowerCase()?.includes('year')
    ) {
      return `${title ? `${title} ` : ''}${EXTENDED_ACCESS_VERBOSE}`;
    }
    return durationAppendedProductTitle(
      duration,
      durationPeriod,
      title
    );
  }

  return title || '';
}

export function getTunnelProductTerm(term?: string) {
  if (!term) {
    return '';
  }
  return (term === '-1' || term?.toLowerCase() === 'always') ? term : (term || '').match(/\d+/)?.[0];
}

export function getTunnelProductTitleVerbose(
  title?: string,
  product?: ProductCardData | PaymentOrderItemData | PaymentOrderItemsProductMetaData | ProductIdentification
) {
  return title
    + `${product?.isConnectSharpenBundle ? ' (Connect with Sharpen)': ''}`
    + (product ? getSharpenCompanionTitle(product) : '');
}

export function getProductTerm(product: ProductTermData) {
  if (!product?.term) {
    return '';
  }

  if (product?.itemType === ItemType.ltia && !product?.offerType) {
    return EXTENDED_ACCESS_DURATION;
  }
  return product?.term;
}

/**
 * Maps the format description from the product (or item) extension attributes in the API responses.
 *
 * @param extensionAttributes
 */
export function mapFormatDescription(extensionAttributes: any): any {
  if (OfferTypeChecker.isCreateEbook(extensionAttributes?.offer_type)) {
    return mapJsonCreateProductFormat(extensionAttributes?.delivery_type);
  }

  return extensionAttributes?.format_desc;
}

/**
 * Maps the editionOrRelease field from extension attributes.
 *
 * A product can only have an edition or a release, never both, so the edition takes
 * precedence when present and valid; otherwise it falls back to the release id.
 * Evergreen (release-based) products carry a non-meaningful placeholder edition value,
 * so is_evergreen always defers to the release id, matching the existing title-rendering
 * guard (see order-history.ts's getTitle: `!item.isEvergreen`).
 */
export function mapEditionOrRelease(extensionAttributes: any): string | undefined {
  const edition = extensionAttributes?.edition;
  const hasValidEdition =
    !extensionAttributes?.is_evergreen && edition !== undefined && edition !== null && String(edition) !== '0';

  return hasValidEdition
    ? `${ordinalSuffixOf(edition)} Edition`
    : extensionAttributes?.release_details?.[0]?.id;
}

/**
 * Maps duration-related fields from extension attributes.
 * Returns an empty object for standalone ALEKS tunnel purchases which should not expose duration data.
 */
export function mapDurationFromExtensionAttributes(extensionAttributes: any): ProductDuration {
  if (
    isStandaloneAleksTunnelPurchase(
      extensionAttributes?.item_type,
      extensionAttributes?.offer_type,
      extensionAttributes?.is_sharpen_companion,
      extensionAttributes?.application
    )
  ) {
    return {};
  }

  return {
    duration: parseInt(extensionAttributes?.duration),
    durationPeriod: extensionAttributes?.duration_unit,
    term: extensionAttributes?.term,
  };
}
